// frontend/src/utils/phoneUtils.js
// Shared phone validation utility — mirrors backend logic in backend/src/utils/phoneValidation.js.
// Used by: AccountSettings, InviteRegistration, ContactVerificationSection, InviteUsers,
//          NotificationChannelConfig, UserNotificationPreferences.

/**
 * Check if a phone number is international (not US/Canada +1)
 * @param {string} phone - Phone number in any format
 * @returns {boolean} true if international, false if US/Canada or invalid
 */
export function isInternationalNumber(phone) {
  if (!phone) return false;
  const normalized = formatToE164(phone);
  // If we can't normalize, treat as international (safer to block than send to unknown)
  if (!normalized) return true;
  return !normalized.startsWith('+1');
}

/**
 * Format a phone number to E.164 format (+[country_code][number])
 * @param {string} phone - Phone number in any format
 * @returns {string|null} E.164 formatted phone or null if invalid
 */
export function formatToE164(phone) {
  if (!phone) return null;

  // Remove all non-numeric characters
  let cleaned = phone.replace(/\D/g, '');

  // If 10 digits, assume US/Canada and prepend country code
  if (cleaned.length === 10) {
    cleaned = '1' + cleaned;
  }

  // E.164 requires 10-15 digits (including country code)
  if (cleaned.length < 10 || cleaned.length > 15) {
    return null;
  }

  return '+' + cleaned;
}

/**
 * Validate phone format and return structured result
 * @param {string} phone - Phone number in any format
 * @returns {Object} { valid, formatted, isInternational, reason }
 */
export function validatePhoneFormat(phone) {
  if (!phone) {
    return { valid: false, formatted: null, isInternational: null, reason: 'empty' };
  }

  const formatted = formatToE164(phone);
  if (!formatted) {
    return { valid: false, formatted: null, isInternational: null, reason: 'invalid_format' };
  }

  return {
    valid: true,
    formatted,
    isInternational: !formatted.startsWith('+1'),
    reason: null,
  };
}
